import { getScrollEventTarget, getScrollTop, scrollContainerMediaQuery } from '@theme/scroll-container';
import { lockScroll, unlockScroll } from '@theme/utilities';

/** Page scroll, in pixels, before the homepage tab row leaves. */
const NAV_COLLAPSE_DISTANCE = 24;

/** Share of the last tab's travel still left when the bar starts filling. */
const BAR_OVERLAP = 0.65;

/** How far through the bar's exit the tabs start coming back. */
const TAB_RETURN_AT = 0.65;
const DESKTOP_NAV_QUERY = '(min-width: 750px)';

class SkreyHeaderMenu extends HTMLElement {
  /** @type {boolean} */
  #headerDocked = false;

  /** Scroll wants the tab row gone. The attributes lag this so the bar can finish first. */
  #wantsCollapsed = false;

  /** @type {boolean} */
  #drawerOpen = false;

  /** @type {number} */
  #tabCount = 0;

  /** @type {HTMLElement | null} */
  #tabs = null;

  /** @type {HTMLElement | null} */
  #underlay = null;

  /** @type {ResizeObserver | null} */
  #tabTravelObserver = null;

  /** @type {number | null} */
  #barStartTimer = null;

  /** @type {number | null} */
  #tabReturnTimer = null;

  /** @type {number} */
  #barPx = 0;

  /** @type {number} */
  #travelPx = 0;

  /** @type {number | null} */
  #navScrollFrame = null;

  /** @type {EventTarget | null} */
  #navScrollTarget = null;

  /** @type {MediaQueryList} */
  #desktopNavQuery = window.matchMedia(DESKTOP_NAV_QUERY);

  /** @type {MutationObserver | null} */
  #mobileMenuObserver = null;

  /** @type {number | null} */
  #sheetFrame = null;

  /** @type {number | null} */
  #sheetWidthFrom = null;

  /** @type {number | null} */
  #sheetScaleFrame = null;

  /** @type {number} */
  #tabEnterFrame = 0;

  connectedCallback() {
    this.header = this.closest('header-component');
    this.drawer = this.querySelector('[data-skrey-desktop-drawer]');
    this.body = this.querySelector('[data-skrey-desktop-body]');
    this.mobileList = this.header?.querySelector('.skrey-header__drawer-list');
    this.activeSource = null;

    this.header?.addEventListener('click', this.#onClick, true);
    document.addEventListener('keydown', this.#onKey);
    window.addEventListener('resize', this.#onResize);
    this.body?.addEventListener('click', this.#onAccordionClick, true);
    this.#watchMobileMenu();
    this.#fillMobile();
    this.#bindNavCollapse();
  }

  disconnectedCallback() {
    this.header?.removeEventListener('click', this.#onClick, true);
    document.removeEventListener('keydown', this.#onKey);
    window.removeEventListener('resize', this.#onResize);
    this.body?.removeEventListener('click', this.#onAccordionClick, true);
    if (this.#sheetFrame != null) cancelAnimationFrame(this.#sheetFrame);
    if (this.#sheetScaleFrame != null) cancelAnimationFrame(this.#sheetScaleFrame);
    this.#sheetFrame = null;
    this.#sheetScaleFrame = null;
    this.drawer?.removeEventListener('transitionend', this.#onDrawerFadeEnd);
    this.#mobileMenuObserver?.disconnect();
    this.#mobileMenuObserver = null;
    this.#unpinMobileHeader();
    this.#unbindNavCollapse();
    this.#undockHeader();
    this.#unlockScroll();
  }

  #onClick = (event) => {
    if (this.#closeIfOutsideDrawer(event)) return;

    const menuToggle = event.target.closest('.skrey-header__menu-toggle');
    if (menuToggle && !window.matchMedia('(min-width: 750px)').matches) {
      const details = menuToggle.closest('details');
      if (details && !details.classList.contains('menu-open')) this.#pinMobileHeader();
      return;
    }

    if (menuToggle && window.matchMedia('(min-width: 750px)').matches) {
      event.preventDefault();
      event.stopPropagation();
      const details = menuToggle.closest('details');
      if (details) {
        details.open = false;
        details.classList.remove('menu-open');
      }
      if (this.header?.hasAttribute('data-drawer-open')) {
        this.#close();
        return;
      }
      const tab =
        this.querySelector('.skrey-nav-source--highlight [data-skrey-tab]') || this.querySelector('[data-skrey-tab]');
      if (tab) this.#toggleTab(tab);
      return;
    }

    const tab = event.target.closest('[data-skrey-tab]');
    if (tab && this.header?.contains(tab)) {
      event.preventDefault();
      this.#toggleTab(tab);
      return;
    }

    if (event.target.closest('[data-skrey-drawer-close]')) {
      this.#close();
    }
  };

  /**
   * The header bar paints above the backdrop, so logo, search, and cart would
   * otherwise stay clickable. Anything outside the sheet closes the drawer.
   * The tab row stays open so a click there changes tabs instead of dismissing.
   * @param {Event} event
   */
  #closeIfOutsideDrawer(event) {
    if (!this.header?.hasAttribute('data-drawer-open')) return false;
    if (!window.matchMedia('(min-width: 750px)').matches) return false;

    const sheet = this.drawer?.querySelector('.skrey-desktop-drawer__sheet');
    if (sheet?.contains(event.target)) return false;
    if (event.target.closest('.skrey-header__nav, .skrey-header__menu-toggle, .skrey-desktop-drawer__close')) {
      return false;
    }

    event.preventDefault();
    event.stopPropagation();
    this.#close();
    return true;
  }

  #onKey = (event) => {
    if (event.key === 'Escape' && this.header?.hasAttribute('data-drawer-open')) {
      this.#close();
    }
  };

  #toggleTab(tab) {
    if (!window.matchMedia('(min-width: 750px)').matches) return;

    const source = tab.closest('.skrey-nav-source');
    if (!source) return;

    if (this.activeSource === source) {
      this.#close();
      return;
    }

    this.#open(source);
  }

  #open(source) {
    const panel = source.querySelector('.skrey-nav-source__panel');
    if (!panel || !this.body || !this.drawer) return;

    const sheet = this.drawer.querySelector('.skrey-desktop-drawer__sheet');
    const switching = this.drawer.hasAttribute('data-open') && !this.drawer.hidden;
    if (switching) {
      this.#sheetWidthFrom = this.#paintedPanelWidth();
    } else if (sheet instanceof HTMLElement) {
      sheet.style.removeProperty('width');
      this.drawer.style.setProperty('--skrey-drawer-scale', '1');
    }

    const motion = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const outgoing = switching && motion ? this.body.querySelector('.skrey-nav-source__panel') : null;
    const shift = switching ? this.#tabShift(source) : 0;
    const ghost = outgoing instanceof HTMLElement && outgoing !== panel ? outgoing.cloneNode(true) : null;

    if (this.activeSource && this.activeSource !== source) {
      this.#park(this.activeSource);
    }

    panel.hidden = false;
    this.body.replaceChildren(panel);
    this.#openAccordions(panel);
    if (ghost instanceof HTMLElement) this.#fadeTabGhost(ghost, shift);
    if (switching && motion) this.#enterTabPanel(panel, shift);
    this.activeSource = source;

    this.querySelectorAll('[data-skrey-tab]').forEach((button) => {
      button.setAttribute('aria-expanded', button === source.querySelector('[data-skrey-tab]') ? 'true' : 'false');
    });

    this.#revealDrawer();
    this.#setBarDelay(false);
    this.header?.setAttribute('data-drawer-open', '');
    this.#syncNavCollapse();
    if (this.header) void this.header.offsetHeight;
    this.#resetMenuToggle();
    requestAnimationFrame(() => {
      if (this.header?.hasAttribute('data-drawer-open')) this.#resetMenuToggle();
    });
    this.#dockHeader();
    this.#placeSheet();
    if (switching && sheet instanceof HTMLElement) this.#animateSheetWidth();
    this.#lockScroll();
  }

  #revealDrawer() {
    if (!this.drawer) return;

    this.drawer.removeEventListener('transitionend', this.#onDrawerFadeEnd);
    const alreadyOpen = this.drawer.hasAttribute('data-open') && !this.drawer.hidden;
    this.drawer.hidden = false;
    if (alreadyOpen) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.drawer.setAttribute('data-open', '');
      return;
    }

    void this.drawer.offsetWidth;
    this.drawer.setAttribute('data-open', '');
  }

  #concealDrawer() {
    if (!this.drawer) return;

    this.drawer.removeAttribute('data-open');
    if (this.drawer.hidden) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.drawer.hidden = true;
      return;
    }

    this.drawer.addEventListener('transitionend', this.#onDrawerFadeEnd);
  }

  /** @param {TransitionEvent} event */
  #onDrawerFadeEnd = (event) => {
    if (event.target !== this.drawer || event.propertyName !== 'opacity') return;
    this.drawer?.removeEventListener('transitionend', this.#onDrawerFadeEnd);
    if (!this.drawer?.hasAttribute('data-open')) this.drawer.hidden = true;
  };

  #resetMenuToggle() {
    const details = this.header?.querySelector('#Details-menu-drawer-container');
    if (!details) return;
    details.open = false;
    details.classList.remove('menu-open');
    const expanded = this.header?.hasAttribute('data-drawer-open') ?? false;
    details.querySelector('summary')?.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  #close() {
    if (this.#tabEnterFrame) cancelAnimationFrame(this.#tabEnterFrame);
    this.#tabEnterFrame = 0;
    this.body?.querySelector('.skrey-nav-source__panel')?.classList.remove('is-entering');
    this.body?.querySelectorAll('.skrey-drawer__panel-ghost').forEach((node) => node.remove());
    if (this.activeSource) this.#park(this.activeSource);
    this.activeSource = null;
    this.querySelectorAll('[data-skrey-tab]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    this.#concealDrawer();
    this.#setBarDelay(true);
    this.#releaseLogoDelay();
    this.header?.removeAttribute('data-drawer-open');
    this.#resetMenuToggle();
    requestAnimationFrame(() => this.#resetMenuToggle());
    this.#undockHeader();
    this.#unlockScroll();
    this.#syncNavCollapse();
  }

  /**
   * Tabs sit in a row, so the list eases a few pixels toward the tab you chose.
   * @param {Element} nextSource
   * @returns {number}
   */
  #tabShift(nextSource) {
    const sources = [...this.querySelectorAll('.skrey-nav-source')];
    const from = sources.indexOf(this.activeSource);
    const to = sources.indexOf(nextSource);
    if (from < 0 || to < 0 || from === to) return 8;
    const sign = to > from ? 1 : -1;
    const rtl = document.documentElement.dir === 'rtl';
    return (rtl ? -sign : sign) * 8;
  }

  /**
   * @param {HTMLElement} ghost
   * @param {number} shift
   */
  #fadeTabGhost(ghost, shift) {
    this.body?.querySelectorAll('.skrey-drawer__panel-ghost').forEach((node) => node.remove());
    ghost.hidden = false;
    ghost.setAttribute('aria-hidden', 'true');
    ghost.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
    ghost.classList.add('skrey-drawer__panel-ghost');
    ghost.style.setProperty('--skrey-tab-shift', `${shift}px`);
    this.body?.append(ghost);
    requestAnimationFrame(() => ghost.classList.add('is-gone'));
    ghost.addEventListener('transitionend', (event) => {
      if (event.propertyName === 'opacity') ghost.remove();
    });
  }

  /**
   * @param {HTMLElement} panel
   * @param {number} shift
   */
  #enterTabPanel(panel, shift) {
    if (this.#tabEnterFrame) cancelAnimationFrame(this.#tabEnterFrame);

    panel.style.setProperty('--skrey-tab-shift', `${shift}px`);
    panel.classList.add('is-entering');
    panel.style.transition = 'none';
    void panel.offsetWidth;
    panel.style.removeProperty('transition');
    this.#tabEnterFrame = requestAnimationFrame(() => {
      this.#tabEnterFrame = 0;
      panel.classList.remove('is-entering');
    });
  }

  #park(source) {
    const panel = this.body?.querySelector('.skrey-nav-source__panel');
    if (!panel) return;
    panel.hidden = true;
    source.append(panel);
  }

  /**
   * Every group starts expanded. Reopening a tab restores that.
   * @param {HTMLElement} panel
   */
  #openAccordions(panel) {
    for (const accordion of panel.querySelectorAll('.skrey-drawer__accordion')) {
      if (accordion instanceof HTMLDetailsElement) accordion.open = true;
    }
  }

  /**
   * A partly scrolled header sits above the screen. The sheet is positioned
   * from that header, so pull the header down to the top while the menu is open.
   */
  /**
   * Locking scroll sets overflow:hidden on the page, which releases a sticky
   * header back to its place above the viewport. Measure before that lock and
   * fix the section where it already is.
   */
  #watchMobileMenu() {
    const details = this.header?.querySelector('#Details-menu-drawer-container');
    if (!(details instanceof HTMLElement)) return;

    this.#mobileMenuObserver = new MutationObserver(() => {
      if (!details.classList.contains('menu-open')) this.#unpinMobileHeader();
    });
    this.#mobileMenuObserver.observe(details, { attributes: true, attributeFilter: ['class'] });
  }

  #pinMobileHeader() {
    const section = this.header?.closest('.header-section');
    if (!(section instanceof HTMLElement) || section.hasAttribute('data-skrey-pinned')) return;

    const top = section.getBoundingClientRect().top;
    if (top > 0) return;

    section.setAttribute('data-skrey-pinned', '');
    section.style.position = 'fixed';
    section.style.top = '0';
    section.style.left = '0';
    section.style.right = '0';
    section.style.zIndex = 'var(--layer-menu-drawer)';
  }

  #unpinMobileHeader() {
    const section = this.header?.closest('.header-section');
    if (!(section instanceof HTMLElement) || !section.hasAttribute('data-skrey-pinned')) return;

    section.removeAttribute('data-skrey-pinned');
    section.style.position = '';
    section.style.top = '';
    section.style.left = '';
    section.style.right = '';
    section.style.zIndex = '';
  }

  #dockHeader() {
    if (!this.header || this.#headerDocked) return;

    const offset = Math.round(-this.header.getBoundingClientRect().top);
    if (offset <= 1) return;

    this.header.style.setProperty('--skrey-header-dock', `${offset}px`);
    this.#headerDocked = true;
  }

  #undockHeader() {
    this.header?.style.removeProperty('--skrey-header-dock');
    this.#headerDocked = false;
  }

  #placeSheet() {
    const sheet = this.drawer?.querySelector('.skrey-desktop-drawer__sheet');
    if (!sheet || !this.header) return;

    const nav = this.querySelector('.skrey-header__nav');
    const clip = this.querySelector('.skrey-header__tabs-clip');
    const tabRow = this.querySelector('.skrey-header__tabs');
    let height = this.header.offsetHeight;
    const tabRowOverlay = tabRow instanceof HTMLElement && getComputedStyle(tabRow).position === 'absolute';
    if (tabRowOverlay) {
      const headerTop = this.header.getBoundingClientRect().top;
      height = Math.round(tabRow.getBoundingClientRect().bottom - headerTop);
    } else if (nav && clip && clip.offsetHeight + 1 < nav.offsetHeight) {
      // The row opens on the next frame. Count the tabs' layout height when the
      // clip has not caught up yet, so the sheet starts below them.
      height += nav.offsetHeight - clip.offsetHeight;
    }
    sheet.style.setProperty('--skrey-drawer-offset', `${Math.round(height)}px`);

    /* The sheet is fixed inside the header, which sits below the announcement bar.
       A full viewport height would run past the window by that offset. */
    const sheetInset = Math.max(0, Math.round(this.header.getBoundingClientRect().top));
    this.header.style.setProperty('--skrey-sheet-inset', `${sheetInset}px`);

    const cap = document.documentElement.clientWidth;
    let tabWidth = 360;
    const tabs = nav?.querySelectorAll('.skrey-header__tab');
    if (tabs?.length) {
      let end = 0;
      tabs.forEach((tab) => {
        end = Math.max(end, tab.getBoundingClientRect().right);
      });
      const start = sheet.getBoundingClientRect().left;
      tabWidth = Math.ceil(end - start + 40);
    }

    /* The columns set the width. This only keeps the sheet from being narrower than the tabs. */
    sheet.style.setProperty('--skrey-drawer-min', `${Math.min(cap, Math.max(tabWidth, 360))}px`);
    this.drawer?.style.setProperty('--skrey-drawer-span', `${sheet.offsetWidth}px`);
  }

  /**
   * The details toggle event arrives after the browser has already painted the new
   * links at the previous width. Prepare on click, then size before that paint.
   * @param {Event} event
   */
  #onAccordionClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('.skrey-drawer__accordion-summary')) return;
    if (!this.drawer?.hasAttribute('data-open')) return;

    this.#sheetWidthFrom = this.#paintedPanelWidth();
    if (this.#sheetScaleFrame != null) cancelAnimationFrame(this.#sheetScaleFrame);
    this.#sheetScaleFrame = requestAnimationFrame(() => {
      this.#sheetScaleFrame = null;
      this.#animateSheetWidth();
    });
  };

  /** @returns {number | null} */
  #paintedPanelWidth() {
    const panel = this.drawer?.querySelector('.skrey-desktop-drawer__panel');
    return panel instanceof HTMLElement ? panel.getBoundingClientRect().width : null;
  }

  /**
   * The sheet takes its final width immediately. The white panel scales in CSS.
   */
  #animateSheetWidth() {
    const sheet = this.drawer?.querySelector('.skrey-desktop-drawer__sheet');
    const drawer = this.drawer;
    const from = this.#sheetWidthFrom;
    this.#sheetWidthFrom = null;
    if (!(sheet instanceof HTMLElement) || !(drawer instanceof HTMLElement) || from == null) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      sheet.style.removeProperty('width');
      drawer.style.setProperty('--skrey-drawer-scale', '1');
      drawer.style.setProperty('--skrey-drawer-span', `${sheet.offsetWidth}px`);
      return;
    }

    sheet.style.removeProperty('width');
    const to = sheet.offsetWidth;
    sheet.style.width = `${to}px`;
    if (to < 1 || Math.abs(to - from) < 1) {
      drawer.style.setProperty('--skrey-drawer-scale', '1');
      drawer.style.setProperty('--skrey-drawer-span', `${to}px`);
      return;
    }

    drawer.style.setProperty('--skrey-drawer-span', `${to}px`);
    drawer.style.transition = 'none';
    drawer.style.setProperty('--skrey-drawer-scale', String(from / to));
    void drawer.offsetWidth;
    drawer.style.removeProperty('transition');
    if (this.#sheetScaleFrame != null) cancelAnimationFrame(this.#sheetScaleFrame);
    this.#sheetScaleFrame = requestAnimationFrame(() => {
      this.#sheetScaleFrame = null;
      drawer.style.setProperty('--skrey-drawer-scale', '1');
    });
  }

  #onResize = () => {
    if (!this.header?.hasAttribute('data-drawer-open')) return;
    if (this.#sheetFrame != null) return;
    this.#sheetFrame = requestAnimationFrame(() => {
      this.#sheetFrame = null;
      if (this.header?.hasAttribute('data-drawer-open')) this.#placeSheet();
    });
  };

  #lockScroll() {
    if (this.drawer) lockScroll(this.drawer);
  }

  #unlockScroll() {
    if (this.drawer) unlockScroll(this.drawer);
  }

  #fillMobile() {
    if (!this.mobileList || !this.header) return;
    this.mobileList.replaceChildren();
    this.header.querySelectorAll('[data-skrey-drawer-item]').forEach((template) => {
      if (template.content) this.mobileList.append(template.content.cloneNode(true));
    });
  }

  /**
   * Homepage desktop only. The tab row is in the document on every page so the
   * drawer can still read it; other templates hide it in CSS.
   */
  #bindNavCollapse() {
    if (!this.header?.hasAttribute('data-home')) return;

    this.#tabs = this.querySelector('.skrey-header__tabs');
    this.#underlay = this.header.querySelector('.header__underlay-closed');
    this.#indexTabs();
    this.#measureTabTravel();
    this.#measureBarHeight();
    const nav = this.querySelector('.skrey-header__nav');
    const top = this.header.querySelector('.skrey-header__top');
    if (this.#tabTravelObserver === null) {
      this.#tabTravelObserver = new ResizeObserver(() => {
        this.#measureTabTravel();
        this.#measureBarHeight();
      });
      if (nav instanceof HTMLElement) this.#tabTravelObserver.observe(nav);
      if (top instanceof HTMLElement) this.#tabTravelObserver.observe(top);
    }
    this.#underlay?.addEventListener('transitionend', this.#onBarMotionEnd);
    this.#desktopNavQuery.addEventListener('change', this.#syncNavCollapse);
    scrollContainerMediaQuery.addEventListener('change', this.#rebindNavScroll);
    this.#rebindNavScroll(getScrollTop() > NAV_COLLAPSE_DISTANCE);
  }

  /** Stagger indexes are static. The animated property is translate, not these variables. */
  #indexTabs() {
    const sources = this.querySelectorAll('.skrey-header__nav > .skrey-nav-source');
    this.#tabCount = sources.length;
    sources.forEach((source, index) => {
      if (source instanceof HTMLElement) source.style.setProperty('--skrey-tab-index', String(index));
    });
    const count = String(sources.length);
    this.header?.style.setProperty('--skrey-tab-count', count);
    this.#tabs?.style.setProperty('--skrey-tab-count', count);
  }

  /**
   * Labels move by the row's height, not their own, so the padding under them
   * does not leave the bottoms in view. The length is measured, never animated.
   */
  #measureTabTravel() {
    if (this.header?.hasAttribute('data-nav-collapsed')) return;
    const nav = this.querySelector('.skrey-header__nav');
    if (!(nav instanceof HTMLElement) || !this.#tabs) return;
    const height = Math.ceil(nav.getBoundingClientRect().height);
    if (height > 0 && height !== this.#travelPx) {
      this.#travelPx = height;
      this.header?.style.setProperty('--skrey-tabs-travel', `${height}px`);
      this.#syncBarBox();
    }
  }

  /** Logo-row height. The painted bar scales; these lengths are not animated. */
  #measureBarHeight() {
    const top = this.header?.querySelector('.skrey-header__top');
    if (!(top instanceof HTMLElement)) return;
    const height = Math.round(top.getBoundingClientRect().height);
    if (height > 0 && height !== this.#barPx) {
      this.#barPx = height;
      this.#syncBarBox();
    }
  }

  /** One layout size for the bar. The visible amount is a scale, from 0 to the logo row to the full row. */
  #syncBarBox() {
    if (!this.header || this.#barPx <= 0) return;
    const open = this.#barPx + this.#travelPx;
    this.header.style.setProperty('--skrey-bar-height', `${this.#barPx}px`);
    this.header.style.setProperty('--skrey-bar-open', `${open}px`);
    this.header.style.setProperty('--skrey-bar-scale-rest', (this.#barPx / open).toFixed(4));
  }

  /**
   * @param {string} value
   * @param {number} fallback
   */
  #cssMs(value, fallback) {
    const raw = value.trim();
    if (raw.endsWith('ms')) return parseFloat(raw);
    if (raw.endsWith('s')) return parseFloat(raw) * 1000;
    return fallback;
  }

  /** Wait until the last tab is nearly gone, then let the bar start. */
  #barLead() {
    const styles = getComputedStyle(this.#tabs ?? this);
    const stagger = this.#cssMs(styles.getPropertyValue('--skrey-tab-stagger'), 45);
    const move = this.#cssMs(styles.getPropertyValue('--skrey-tab-move'), 300);
    const count = Math.max(this.#tabCount, 1);
    return Math.round((count - 1) * stagger + move * (1 - BAR_OVERLAP));
  }

  #cancelBarStart() {
    if (this.#barStartTimer === null) return;
    clearTimeout(this.#barStartTimer);
    this.#barStartTimer = null;
  }

  #cancelTabReturn() {
    if (this.#tabReturnTimer === null) return;
    clearTimeout(this.#tabReturnTimer);
    this.#tabReturnTimer = null;
  }

  #revealTabs() {
    this.header?.removeAttribute('data-nav-collapsed');
    this.header?.removeAttribute('data-wordmark-hidden');
    this.querySelector('.skrey-header__nav')?.removeAttribute('inert');
  }

  /**
   * Must run before the drawer attribute changes. A layout read in between
   * would start the bar transition with the previous delay.
   * @param {boolean} waitForLastTab
   */
  /**
   * At the top there is no bar to wait for. Start the inverse swap with the
   * sheet, then drop the override so a later scroll still waits for the bar.
   */
  #releaseLogoDelay() {
    const header = this.header;
    if (!header || header.hasAttribute('data-nav-bar')) return;

    header.style.setProperty('--skrey-logo-delay', '0s');
    const mark = header.querySelector('.skrey-wordmark__solid');
    const clear = () => header.style.removeProperty('--skrey-logo-delay');
    if (!mark) {
      clear();
      return;
    }

    const onEnd = (event) => {
      if (event.propertyName !== 'opacity') return;
      mark.removeEventListener('transitionend', onEnd);
      clear();
    };
    mark.addEventListener('transitionend', onEnd);
    window.setTimeout(() => {
      mark.removeEventListener('transitionend', onEnd);
      clear();
    }, 400);
  }

  #setBarDelay(waitForLastTab) {
    this.header?.style.setProperty(
      '--skrey-bar-delay',
      waitForLastTab ? 'calc((var(--skrey-tab-count, 1) - 1) * var(--skrey-tab-stagger, 45ms))' : '0s'
    );
  }

  #unbindNavCollapse() {
    this.#navScrollTarget?.removeEventListener('scroll', this.#onNavScroll);
    this.#desktopNavQuery.removeEventListener('change', this.#syncNavCollapse);
    scrollContainerMediaQuery.removeEventListener('change', this.#rebindNavScroll);
    this.#cancelBarStart();
    this.#cancelTabReturn();
    this.#underlay?.removeEventListener('transitionend', this.#onBarMotionEnd);
    this.#tabTravelObserver?.disconnect();
    this.#tabTravelObserver = null;
    if (this.#navScrollFrame !== null) cancelAnimationFrame(this.#navScrollFrame);
    this.#navScrollFrame = null;
    this.#navScrollTarget = null;
  }

  /** @param {boolean | Event} [instant] */
  #rebindNavScroll = (instant) => {
    this.#navScrollTarget?.removeEventListener('scroll', this.#onNavScroll);
    this.#navScrollTarget = getScrollEventTarget();
    this.#navScrollTarget.addEventListener('scroll', this.#onNavScroll, { passive: true });
    this.#syncNavCollapse({ instant: instant === true });
  };

  #onNavScroll = () => {
    if (this.#navScrollFrame !== null) return;

    this.#navScrollFrame = requestAnimationFrame(() => {
      this.#navScrollFrame = null;
      this.#syncNavCollapse();
    });
  };

  /**
   * @param {{ instant?: boolean }} [options]
   */
  #syncNavCollapse = ({ instant = false } = {}) => {
    if (!this.header?.hasAttribute('data-home')) return;

    const drawerOpen = this.header.hasAttribute('data-drawer-open');
    const wantsCollapsed =
      !drawerOpen && this.#desktopNavQuery.matches && getScrollTop() > NAV_COLLAPSE_DISTANCE;
    if (!instant && wantsCollapsed === this.#wantsCollapsed && drawerOpen === this.#drawerOpen) return;

    const drawerChanged = drawerOpen !== this.#drawerOpen;
    this.#wantsCollapsed = wantsCollapsed;
    this.#drawerOpen = drawerOpen;
    this.#applyNavPhase(instant, drawerChanged);
  };

  /**
   * Leave: tabs, then the bar. Return: the bar, then the tabs.
   * Opening the drawer brings the tabs back on the bar that is already up.
   * @param {boolean} instant
   * @param {boolean} [drawerChanged]
   */
  #applyNavPhase(instant, drawerChanged = false) {
    const header = this.header;
    if (!header) return;
    this.#cancelBarStart();
    this.#cancelTabReturn();
    if (!drawerChanged) this.#setBarDelay(false);
    const nav = this.querySelector('.skrey-header__nav');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (header.hasAttribute('data-drawer-open')) {
      header.removeAttribute('data-nav-collapsed');
      header.removeAttribute('data-wordmark-hidden');
      nav?.removeAttribute('inert');
      if (getScrollTop() > NAV_COLLAPSE_DISTANCE && this.#desktopNavQuery.matches) {
        header.setAttribute('data-nav-bar', '');
      } else {
        header.removeAttribute('data-nav-bar');
      }
      return;
    }

    if (!this.#desktopNavQuery.matches) {
      header.removeAttribute('data-nav-collapsed');
      header.removeAttribute('data-nav-bar');
      header.removeAttribute('data-wordmark-hidden');
      nav?.removeAttribute('inert');
      return;
    }

    if (instant || reduce) {
      this.#setPhaseAttributes(header, nav, this.#wantsCollapsed);
      return;
    }

    if (this.#wantsCollapsed) {
      const alreadyHidden = header.hasAttribute('data-nav-collapsed');
      header.setAttribute('data-nav-collapsed', '');
      header.setAttribute('data-wordmark-hidden', '');
      nav?.setAttribute('inert', '');
      if (header.hasAttribute('data-nav-bar')) return;
      if (alreadyHidden) {
        header.setAttribute('data-nav-bar', '');
        return;
      }
      this.#barStartTimer = window.setTimeout(() => {
        this.#barStartTimer = null;
        if (!this.#wantsCollapsed || header.hasAttribute('data-drawer-open')) return;
        if (!header.hasAttribute('data-nav-collapsed')) return;
        header.setAttribute('data-nav-bar', '');
      }, this.#barLead());
      return;
    }

    this.#retractBar();
  }

  /** Drop the bar, then bring the tabs back before it has fully left. */
  #retractBar() {
    const header = this.header;
    if (!header?.hasAttribute('data-nav-bar')) {
      this.#revealTabs();
      return;
    }
    header.removeAttribute('data-nav-bar');
    const underlayHeight = this.#underlay?.getBoundingClientRect().height ?? 0;
    if (underlayHeight < 1) {
      this.#revealTabs();
      return;
    }
    const move = this.#cssMs(getComputedStyle(this.#tabs ?? this).getPropertyValue('--skrey-tab-move'), 300);
    this.#tabReturnTimer = window.setTimeout(() => {
      this.#tabReturnTimer = null;
      if (this.#wantsCollapsed || header.hasAttribute('data-drawer-open')) return;
      if (header.hasAttribute('data-nav-bar')) return;
      this.#revealTabs();
    }, Math.round(move * TAB_RETURN_AT));
  }

  /**
   * @param {HTMLElement} header
   * @param {Element | null} nav
   * @param {boolean} collapsed
   */
  #setPhaseAttributes(header, nav, collapsed) {
    const tabs = this.#tabs;
    const sources = this.querySelectorAll('.skrey-header__nav > .skrey-nav-source');
    const underlay = this.#underlay;
    const held = [tabs, underlay, ...sources].filter((node) => node instanceof HTMLElement);
    held.forEach((node) => node.style.setProperty('transition', 'none'));
    header.toggleAttribute('data-nav-collapsed', collapsed);
    header.toggleAttribute('data-nav-bar', collapsed);
    header.toggleAttribute('data-wordmark-hidden', collapsed);
    nav?.toggleAttribute('inert', collapsed);
    if (header) void header.offsetHeight;
    held.forEach((node) => node.style.removeProperty('transition'));
  }

  /** The bar has left. The tabs can come back into the transparent header. */
  #onBarMotionEnd = (event) => {
    if (event.propertyName !== 'scale') return;
    if (event.target !== this.#underlay) return;
    if (this.#wantsCollapsed || this.header?.hasAttribute('data-drawer-open')) return;
    if (this.header?.hasAttribute('data-nav-bar')) return;
    this.#cancelTabReturn();
    this.#revealTabs();
  };
}

if (!customElements.get('skrey-header-menu')) {
  customElements.define('skrey-header-menu', SkreyHeaderMenu);
}
